import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import MuiDialog from '@mui/material/Dialog';
import { useDataProps, useAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { closeModalOnEscapeKeyDown, triggerModalPrimaryAction } from '@famly/mf_modals-dialogs_modal';
/**
 * Base component for implementing modern famly dialogs.
 *
 * Can take an arbitrary element as children, but it is recommended to use the other dialogs elements defined
 * in this same project
 *
 * Pressing `Escape` closes the dialog and pressing `Cmd`/`Ctrl` + `Enter` triggers its confirm action.
 *
 * @param open tells if the dialog should be shown or not
 * @param onClose called when the user dismisses the dialog with the `Escape` key. Clicking the
 *   backdrop deliberately does not close the dialog, so unsaved input cannot be lost by a stray click.
 * @param zIndex optional override for the dialog's z-index. Escape hatch for the rare
 *   case where an MF Dialog needs to stack below a legacy `PortalModal` (z-index 120)
 *   that it opens as a child.
 */
export const Dialog = ({ open, children, onClose, zIndex, ...rest }) => {
    const dataAttributes = useDataProps(rest);
    const ariaAttributes = useAriaProps(rest);
    return (_jsx(MuiDialog, { open: open, onClose: closeModalOnEscapeKeyDown(onClose), onKeyDown: triggerModalPrimaryAction, ...dataAttributes, ...ariaAttributes, sx: zIndex !== undefined ? { zIndex } : undefined, PaperProps: {
            sx: theme => {
                var _a;
                return ({
                    width: {
                        base: '100%',
                        mobileLandscape: '500px',
                    },
                    margin: {
                        base: 0,
                        mobileLandscape: undefined,
                    },
                    padding: 0,
                    borderRadius: { base: '16px 16px 0 0', mobileLandscape: '16px' },
                    boxShadow: (_a = theme.modernFamlyTheme) === null || _a === void 0 ? void 0 : _a.elevation[4],
                    position: {
                        base: 'fixed',
                        mobileLandscape: undefined,
                    },
                    bottom: {
                        base: 0,
                        mobileLandscape: 'inherit',
                    },
                    maxHeight: '90%',
                });
            },
        }, children: children }));
};
